import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ChannelConnectionResponseDto,
  ListChannelConnectionsResponseDto,
  CreateWhaticketOfficialChannelDto,
  UpdateWhaticketOfficialChannelDto,
  CreateNotificamehubOfficialChannelDto,
  UpdateNotificamehubOfficialChannelDto,
  TestConnectionResponseDto,
  TestConnectionRequestDto,
} from '@/types/channels';
import { ChatProvider } from '@/types/channels';

/**
 * Hook para listar todos os canais
 */
export function useChannelConnections() {
  return useQuery<ListChannelConnectionsResponseDto>({
    queryKey: ['channel-connections'],
    queryFn: async () => {
      const response = await api.get('/channels');
      return response.data;
    },
  });
}

/**
 * Hook para obter um canal específico
 */
export function useChannelConnection(id: string) {
  return useQuery<ChannelConnectionResponseDto>({
    queryKey: ['channel-connections', id],
    queryFn: async () => {
      const response = await api.get(`/channels/whatsapp/whaticket/official/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook para criar canal Whaticket Official
 */
export function useCreateWhaticketOfficial() {
  const queryClient = useQueryClient();

  return useMutation<ChannelConnectionResponseDto, Error, CreateWhaticketOfficialChannelDto>({
    mutationFn: async (data) => {
      const response = await api.post('/channels/whatsapp/whaticket/official', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
}

/**
 * Hook para atualizar canal
 */
export function useUpdateWhaticketOfficial(id: string) {
  const queryClient = useQueryClient();

  return useMutation<ChannelConnectionResponseDto, Error, UpdateWhaticketOfficialChannelDto>({
    mutationFn: async (data) => {
      const response = await api.put(`/channels/whatsapp/whaticket/official/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
      queryClient.invalidateQueries({ queryKey: ['channel-connections', id] });
    },
  });
}

/**
 * Hook para deletar canal
 */
export function useDeleteChannel(id: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: async () => {
      await api.delete(`/channels/whatsapp/whaticket/official/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
}

/**
 * Get the correct endpoint path based on provider
 */
function getProviderEndpointPath(provider: string): string {
  switch (provider) {
    case ChatProvider.NOTIFICAMEHUB:
      return 'notificamehub';
    case ChatProvider.WHATICKET:
    default:
      return 'whaticket';
  }
}

/**
 * Hook para testar conexão
 *
 * @param id Channel connection ID
 * @param provider Channel provider (WHATICKET, NOTIFICAMEHUB, etc.)
 */
export function useTestConnection(id: string, provider: string = ChatProvider.WHATICKET) {
  const queryClient = useQueryClient();
  const providerPath = getProviderEndpointPath(provider);

  return useMutation<TestConnectionResponseDto, Error, TestConnectionRequestDto | undefined>({
    mutationFn: async (data) => {
      const response = await api.post(
        `/channels/whatsapp/${providerPath}/official/${id}/test`,
        data || {}
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate to refresh lastUsedAt
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
}

/**
 * Hook para criar canal NotificameHub Official
 */
export function useCreateNotificamehubOfficial() {
  const queryClient = useQueryClient();

  return useMutation<ChannelConnectionResponseDto, Error, CreateNotificamehubOfficialChannelDto>({
    mutationFn: async (data) => {
      const response = await api.post('/channels/whatsapp/notificamehub/official', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
}

/**
 * Hook para atualizar canal NotificameHub Official
 */
export function useUpdateNotificamehubOfficial(id: string) {
  const queryClient = useQueryClient();

  return useMutation<ChannelConnectionResponseDto, Error, UpdateNotificamehubOfficialChannelDto>({
    mutationFn: async (data) => {
      const response = await api.put(`/channels/whatsapp/notificamehub/official/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-connections'] });
      queryClient.invalidateQueries({ queryKey: ['channel-connections', id] });
    },
  });
}
